import { compact } from 'lodash';
import { ReactNode } from 'react';

import { Event } from '@/react/kubernetes/queries/types';
import { createStore } from '@/react/kubernetes/datatables/default-kube-datatable-store';
import { EventsDatatable } from '@/react/kubernetes/components/EventsDatatable';
import { useEvents } from '@/react/kubernetes/queries/useEvents';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { useTableState } from '@@/datatables/useTableState';
import { Widget } from '@@/Widget';

import { GenericResource } from '../../types';

export const storageKey = 'k8sHelmEventsDatatable';
export const settingsStore = createStore(storageKey, {
  id: 'Date',
  desc: true,
});

type Props = {
  namespace: string;
  releaseResources: GenericResource[];
  /** When false, show all events in the namespace instead of only those for the
   * release's resources — used when no release exists to correlate against.
   */
  filterByResources?: boolean;
  /** environmentId defaults to the current route's id. Callers rendered outside
   * a helm release route (e.g. the add-on details view) pass it explicitly.
   */
  environmentId?: EnvironmentId;
  /** isLoading lets a caller reflect work it does before the events query can
   * run (e.g. resolving the environment/release) in the table's loading state.
   */
  isLoading?: boolean;
  /** When standalone, the table renders as its own widget with a header
   * (title + icon), matching other list views. Otherwise it renders headerless
   * to sit inside the caller's own widget (the helm release tabs).
   */
  standalone?: boolean;
  /** Header content — caller-defined. The helm release tabs pass a tip as the
   * title; the add-on view passes neither, keeping the default 'Events' header.
   */
  title?: ReactNode;
  description?: ReactNode;
};

export function HelmEventsDatatable({
  namespace,
  releaseResources,
  filterByResources = true,
  environmentId: environmentIdProp,
  isLoading = false,
  standalone = false,
  title,
  description,
}: Props) {
  const routeEnvironmentId = useEnvironmentId(false);
  const environmentId = environmentIdProp ?? routeEnvironmentId;
  const tableState = useTableState(settingsStore, storageKey);

  const eventsQuery = useEvents(environmentId, {
    namespace,
    queryOptions: {
      // environmentId is 0 until an off-route caller (add-ons) resolves it;
      // firing then would hit /kubernetes/0/... and 404.
      enabled: !!environmentId,
      autoRefreshRate: tableState.autoRefreshRateMS,
      select: (data) =>
        filterByResources ? filterRelatedEvents(data, releaseResources) : data,
    },
  });

  if (standalone) {
    return (
      <EventsDatatable
        dataset={eventsQuery.data || []}
        title={title}
        description={description}
        tableState={tableState}
        isLoading={isLoading || eventsQuery.isInitialLoading}
        data-cy="k8sAppDetail-eventsTable"
      />
    );
  }

  return (
    <Widget>
      <EventsDatatable
        dataset={eventsQuery.data || []}
        title={title}
        titleIcon={null}
        description={description}
        tableState={tableState}
        isLoading={isLoading || eventsQuery.isInitialLoading}
        data-cy="k8sAppDetail-eventsTable"
        // no widget to avoid extra padding from app/react/components/datatables/TableContainer.tsx
        noWidget
      />
    </Widget>
  );
}

export function useHelmEventsTableState() {
  return useTableState(settingsStore, storageKey);
}

export function filterRelatedEvents(
  events: Event[],
  resources: GenericResource[]
) {
  const relatedUids = getReleaseUids(resources);
  const relatedUidsSet = new Set(relatedUids);
  return events.filter(
    (event) =>
      event.involvedObject.uid && relatedUidsSet.has(event.involvedObject.uid)
  );
}

function getReleaseUids(resources: GenericResource[]) {
  return compact(resources.map((resource) => resource.metadata.uid));
}
